import React, { FormEvent } from 'react';

import { useFormik } from 'formik-next';
import * as Yup from 'yup';

import { theme } from '@waglabs/base-theme';
import { Box } from '@waglabs/box-next';
import { Divider } from '@waglabs/divider';
import { Grid } from '@waglabs/grid';
import { TextField } from '@waglabs/textfield';
import { Typography } from '@waglabs/typography';

import { Autocomplete } from '../../../../../../../components/Mapbox/Autocomplete';
import {
  CountryAlpha2ToAlpha3,
  DataTestIds,
} from '../../../../../../../constants';
import {
  MapboxGeocodingFeature,
  ParsedAddress,
} from '../../../../../../../utils/mapbox/geocoding';
import { StepFooter } from '../../StepFooter';

export const FormFields = {
  StreetAddress: 'streetAddress',
  SuiteOrApartment: 'suiteOrApartment',
  City: 'city',
  State: 'state',
  ZipCode: 'zipCode',
  Country: 'country',
};

export const schema = Yup.object().shape({
  [FormFields.StreetAddress]: Yup.string()
    .label('Street address')
    .required(),
  [FormFields.SuiteOrApartment]: Yup.string()
    .label('Suite or apt #'),
});

export type AddressInfoFormProps = {
  formik: ReturnType<typeof useFormik>;
};
export const AddressInfoForm = ({
  formik,
}: AddressInfoFormProps) => {
  const handleOnAutocompleteChange = (params: {
    address: ParsedAddress | undefined;
    label: string;
    value: MapboxGeocodingFeature;
  } | null) => {
    const newValues = {
      [FormFields.StreetAddress]: params?.address?.streetAddress || '',
      [FormFields.State]: params?.address?.state || '',
      [FormFields.City]: params?.address?.city || '',
      [FormFields.ZipCode]: params?.address?.zipCode || '',
      [FormFields.Country]:
      CountryAlpha2ToAlpha3[params?.address?.country as keyof typeof CountryAlpha2ToAlpha3]
      || '',
    };
    formik.setValues(newValues, true);
  };

  return (
    <Box
      as="form"
      onSubmit={(event) => {
        formik.handleSubmit(event as FormEvent<HTMLFormElement>);
      }}
    >
      <Grid
        as="fieldset"
        gridGap="8px"
        p={0}
      >
        <Autocomplete
          placeholder="Street address"
          onChange={handleOnAutocompleteChange}
          id={DataTestIds.Signup.AddressInfo.Street}
          name={DataTestIds.Signup.AddressInfo.Street}
        />
        <TextField
          label="Suite or apt#"
          name={FormFields.SuiteOrApartment}
          onChange={formik.handleChange}
          data-testId={DataTestIds.Signup.AddressInfo.SuiteOrApt}
          fullWidth
        />

        {formik.status?.error && (
          <Typography
            color={theme.colors.red}
            textAlign="center"
            m={0}
            py="16px"
          >
            {formik.status?.error}
          </Typography>
        )}
      </Grid>

      <Divider
        width={1}
      />
      <StepFooter
        primaryCtaText="Done"
        primaryCtaProps={{
          disabled: !formik.isValid || formik.isSubmitting,
          type: 'submit',
          ...{
            'data-testid': DataTestIds.Signup.AddressInfo.SubmitBtn,
          },
        }}
      />
    </Box>
  );
};
